import React from 'react';

const Trainings: React.FC = () => {
  const trainings = [
    {
      id: "liberating-structures",
      title: "Liberating Structures",
      oneLiner: "Unlock your team's full potential with Liberating Structures—simple tools for extraordinary collaboration!",
      description: "Liberating Structures are innovative and powerful facilitation methods designed to enhance engagement, foster inclusivity, and drive effective decision-making in teams. This hands-on, interactive training provides participants with a toolkit of practical techniques to transform meetings and workshops into dynamic, productive sessions. Ideal for IT professionals, project managers, agile coaches, and leaders looking to energize their teams and improve communication within large organizational structures.",
      participants: [
        "Enhanced collaborative and facilitation skills",
        "Practical methods for increasing team engagement", 
        "Strategies to ensure inclusive and effective communication",
        "Techniques to streamline decision-making and problem-solving"
      ],
      durations: [
        { 
          type: "Compact session", 
          duration: "4 hours", 
          priceGBP: "£235 per participant", 
          priceEUR: "€275 per participant", 
          note: "Group of 10-20 participants" 
        },
        { 
          type: "Comprehensive session", 
          duration: "7 hours", 
          priceGBP: "£385 per participant", 
          priceEUR: "€450 per participant", 
          note: "Group of 10-20 participants" 
        }
      ],
      cta: "Ready to revolutionize your team dynamics? Contact us today to schedule your Liberating Structures training!",
      icon: "fa-users",
      category: "Collaboration"
    },
    {
      id: "public-speaking",
      title: "Public Speaking Mastery",
      oneLiner: "Transform your voice into your most powerful tool—master public speaking with confidence and clarity.",
      description: "Effective public speaking is essential for professionals aiming to inspire, influence, and lead. This immersive training equips participants with the skills to communicate ideas compellingly, manage stage presence, and engage diverse audiences. Through interactive exercises and personalized feedback, attendees will overcome speaking anxieties and develop a commanding presence.",
      participants: [
        "Techniques to manage public speaking anxiety",
        "Strategies for structuring impactful speeches",
        "Enhanced vocal delivery and body language",
        "Methods to engage and persuade audiences",
        "Confidence to speak in various professional settings"
      ],
      durations: [
        { 
          type: "1-day intensive workshop", 
          duration: "7 hours", 
          priceGBP: "£350 per participant", 
          priceEUR: "€400 per participant", 
          note: "Group rates (up to 10 participants): From £3,500 / €4,100 + VAT" 
        },
        { 
          type: "2-day comprehensive course", 
          duration: "14 hours", 
          priceGBP: "£550 per participant", 
          priceEUR: "€650 per participant", 
          note: "Group rates (up to 10 participants): From £6,000 / €7,000 + VAT" 
        }
      ],
      cta: "Elevate your team's communication skills. Contact us to schedule your Public Speaking Mastery training and empower your professionals to speak with impact.",
      icon: "fa-microphone",
      category: "Communication"
    },
    {
      id: "terraform-opentofu",
      title: "Terraform / OpenTofu Bootcamp",
      oneLiner: "Master Infrastructure as Code from zero to secure, production-ready deployments—with Terraform and OpenTofu!",
      description: "This beginner-friendly bootcamp is designed for professionals who want to start their journey with Terraform or OpenTofu with no prior knowledge. Participants will be guided from the very basics of infrastructure as code through advanced patterns, security best practices, and scalable design. The training culminates in the use of the TACOS (Terraform Automation and Collaboration Software) tooling to build efficient and maintainable cloud infrastructure. Perfect for cloud engineers, DevOps practitioners, platform teams, and architects aiming to automate infrastructure provisioning using modern, open-source tools.",
      participants: [
        "Solid understanding of Terraform/OpenTofu core concepts",
        "Hands-on experience creating and managing infrastructure",
        "Best practices for versioning, state management, and collaboration",
        "Security and compliance guidance",
        "Exposure to automation frameworks like TACOS"
      ],
      durations: [
        { 
          type: "1-day introduction workshop", 
          duration: "7 hours", 
          priceGBP: "£4,500", 
          priceEUR: "€5,250", 
          note: "Up to 10 participants per group" 
        },
        { 
          type: "2-day deep-dive course", 
          duration: "14 hours", 
          priceGBP: "£7,500", 
          priceEUR: "€8,750", 
          note: "Up to 10 participants per group" 
        },
        { 
          type: "3-day complete bootcamp", 
          duration: "21 hours", 
          priceGBP: "£10,000", 
          priceEUR: "€11,650", 
          note: "Up to 10 participants per group (includes full-day team project)" 
        }
      ],
      cta: "Build your cloud infrastructure skills with confidence. Book your Terraform/OpenTofu training today and start automating the right way!",
      icon: "fa-code-branch",
      category: "Infrastructure"
    },
    {
      id: "docker-fundamentals",
      title: "Docker Fundamentals & Advanced Use",
      oneLiner: "From zero to production-ready containers—learn Docker the right way, hands-on and from scratch.",
      description: "This practical training is crafted for developers, DevOps engineers, and platform teams who want to master containerization using Docker. The course is suitable for complete beginners, offering a solid foundation on Day 1, and diving into production-grade scenarios on Day 2. Participants will gain a complete understanding of how to build, run, manage, and debug containers effectively in real-world environments. Day 1 covers Docker fundamentals including container concepts, Dockerfiles, building and running containers, debugging, and lifecycle management. Day 2 advances to multistage builds, Docker Compose, optimization techniques, and specialized use cases like ML/AI model runners.",
      participants: [
        "Introduction to containers and Docker engine",
        "Writing efficient Dockerfiles and multistage builds",
        "Building, running, and debugging containers",
        "Managing multi-container apps with Docker Compose",
        "Best practices for image optimization, caching, and tagging",
        "Using Docker for ML/AI: the model runner use case"
      ],
      durations: [
        { 
          type: "1-day Docker Fundamentals", 
          duration: "7 hours", 
          priceGBP: "£4,000", 
          priceEUR: "€4,650", 
          note: "Up to 10 participants per group" 
        },
        { 
          type: "2-day Complete Docker Workshop", 
          duration: "14 hours", 
          priceGBP: "£6,500", 
          priceEUR: "€7,600", 
          note: "Up to 10 participants per group" 
        }
      ],
      cta: "Empower your team with real-world container skills. Book your Docker training and make your deployments portable, fast, and secure!",
      icon: "fa-cube",
      category: "Infrastructure"
    },
    {
      id: "cicd-github-actions",
      title: "Introduction to CI/CD with GitHub Actions",
      oneLiner: "Streamline your software delivery pipeline—learn the foundations of CI/CD through hands-on practice.",
      description: "This introductory course offers a comprehensive foundation in Continuous Integration and Continuous Delivery (CI/CD). Designed for developers, testers, and DevOps professionals, the training explains the why and how of CI/CD pipelines, test automation, pull requests, branching models, and deployment strategies. GitHub Actions is used as the main CI/CD platform to demonstrate real-world applications. This training is ideal for teams new to DevOps and automation, providing practical skills to implement modern development workflows.",
      participants: [
        "A solid understanding of the CI/CD lifecycle",
        "Knowledge of pull requests, merge approvals, and automation triggers",
        "Branching strategies (Git Flow, trunk-based, etc.)",
        "Test automation concepts and integration into CI",
        "Hands-on experience with GitHub Actions workflows",
        "Exposure to blue-green, canary, and rolling deployment strategies"
      ],
      durations: [
        { 
          type: "2-day immersive workshop", 
          duration: "14 hours", 
          priceGBP: "£7,000", 
          priceEUR: "€8,150", 
          note: "Up to 20 participants per group" 
        }
      ],
      cta: "Modernize your software delivery process. Book your CI/CD training today and empower your team to deploy with confidence!",
      icon: "fa-sync-alt",
      category: "Infrastructure"
    },
    {
      id: "linux-foundations",
      title: "Linux Foundations & System Administration",
      oneLiner: "From zero to Linux hero—install, secure and run servers with confidence.",
      description: "This bootcamp takes participants from the absolute basics of the Linux command line to solid system administration competence. Through hands-on labs they will learn core commands, user and permission management, service supervision (systemd), shell scripting in Bash, package management across major distributions, and deployment & upkeep of key services (web servers, databases, logging, monitoring and backups). The course emphasises best practices, security hardening and real-world troubleshooting.",
      participants: [
        "Fluency with essential Linux commands and the filesystem hierarchy",
        "Skills to manage users, groups, permissions and sudo",
        "Experience configuring and monitoring services with systemd",
        "Ability to write and debug Bash scripts to automate tasks",
        "Knowledge of package managers (apt, dnf/yum, pacman, snap)",
        "Practical setup of Nginx/Apache, PostgreSQL/MySQL, log rotation",
        "Techniques for system monitoring, journald & syslog, backups and restore"
      ],
      durations: [
        { 
          type: "3-day Linux Fundamentals & Admin", 
          duration: "21 hours", 
          priceGBP: "£650 per participant", 
          priceEUR: "€750 per participant", 
          note: "Group - 10 to 20 people. Base training for all participants" 
        },
        { 
          type: "5-day Complete Linux Ops Bootcamp", 
          duration: "35 hours", 
          priceGBP: "£1,000 per participant", 
          priceEUR: "€1,150 per participant", 
          note: "Group - 10 to 20 people. Enhanced training with advanced topics and hands-on labs" 
        }
      ],
      cta: "Master Linux system administration from the ground up. Book your Linux training today and empower your team to manage servers with confidence!",
      icon: "fa-terminal",
      category: "Infrastructure"
    }
  ];

  const scrollToTraining = (trainingId: string) => {
    const element = document.getElementById(trainingId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="page trainings">
      <div className="container">
        <div className="hero-section">
          <h1>Professional Training Programs</h1>
          <p className="hero-description">
            Comprehensive training programs designed to upskill your team in modern collaboration techniques, 
            communication skills, and infrastructure automation. Transform your organization's capabilities with 
            our expertly crafted workshops and intensive bootcamps.
          </p>
        </div>

        {/* Training Navigation */}
        <section className="training-navigation-section">
          <h2>Available Training Programs</h2>
          <p className="training-nav-intro">
            Browse my comprehensive training offerings designed to enhance your team's capabilities
          </p>
          
          <div className="training-nav-grid">
            {trainings.map((training) => (
              <div 
                key={training.id} 
                className="training-nav-card"
                data-category={training.category}
                onClick={() => scrollToTraining(training.id)}
              >
                <div className="training-nav-icon">
                  <i className={`fa-solid ${training.icon}`}></i>
                </div>
                <div className="training-nav-content">
                  <div className="training-nav-category">{training.category}</div>
                  <h3>{training.title}</h3>
                  <p>{training.oneLiner}</p>
                  <div className="training-nav-meta">
                    <span className="duration-count">{training.durations.length} option{training.durations.length > 1 ? 's' : ''}</span>
                    <div className="price-from-container">
                      <span className="price-from-label">From</span>
                      <span className="price-from-gbp">{training.durations[0].priceGBP}</span>
                      <span className="price-from-eur">{training.durations[0].priceEUR}</span>
                    </div>
                  </div>
                </div>
                <div className="training-nav-arrow">
                  <i className="fa-solid fa-chevron-right"></i>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Detailed Trainings Section */}
        <section className="trainings-detailed-section">
          <h2>Training Details</h2>
          <div className="trainings-grid">
            {trainings.map((training, index) => (
              <div key={index} id={training.id} className="training-card">
                <div className="training-header">
                  <div className={`training-category-badge ${training.category}`}>{training.category}</div>
                  <h3>{training.title}</h3>
                  <p className="training-oneliner">{training.oneLiner}</p>
                </div>
                
                <div className="training-content">
                  <div className="training-description">
                    <h4>Training Description</h4>
                    <p>{training.description}</p>
                  </div>

                  <div className="training-outcomes">
                    <h4>Participants will gain:</h4>
                    <ul>
                      {training.participants.map((outcome, idx) => (
                        <li key={idx}>{outcome}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="training-options">
                    <h4>Duration & Pricing Options:</h4>
                    <div className="duration-grid">
                      {training.durations.map((option, idx) => (
                        <div key={idx} className="duration-option">
                          <div className="duration-type">{option.type}</div>
                          <div className="duration-time">{option.duration}</div>
                          <div className="duration-pricing">
                            <div className="duration-price-gbp">From {option.priceGBP}</div>
                            <div className="duration-price-eur">From {option.priceEUR}</div>
                          </div>
                          <div className="duration-note">{option.note}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="training-cta">
                    <p className="cta-text">{training.cta}</p>
                    <div className="cta-buttons">
                      <a href="/contact" className="training-contact-btn">
                        <i className="fa-solid fa-envelope"></i>
                        <span>Contact for Booking</span>
                      </a>
                      <button onClick={scrollToTop} className="back-to-top-btn">
                        <i className="fa-solid fa-arrow-up"></i>
                        <span>Back to Top</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Training Benefits Section */}
        <section className="training-benefits-section">
          <div className="benefits-content">
            <h2>Why Choose My Training Programs?</h2>
            <p className="benefits-intro">
              Experience comprehensive, hands-on training designed to deliver immediate value and lasting impact 
              for your team and organization.
            </p>
            <p className="benefits-intro">
              All trainings are conducted in person, combining focused theory discussions with extensive hands-on practice. 
              This format ensures maximum engagement, immediate feedback, and optimal learning outcomes for all participants. However, I can also deliver some of the trainings remotely if needed.
            </p>
            
            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fa-solid fa-user-tie"></i>
                </div>
                <h3>Experienced Trainer</h3>
                <p>
                  Learn from a seasoned professional with extensive real-world experience in DevOps, infrastructure 
                  automation, and team collaboration. Practical insights backed by years of industry expertise.
                </p>
                <div className="benefit-highlights">
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Industry veteran with proven track record
                  </span>
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Real-world scenarios and case studies
                  </span>
                </div>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fa-solid fa-cogs"></i>
                </div>
                <h3>Tailored to Your Needs</h3>
                <p>
                  Every training program is customized to your organization's specific challenges, goals, and technical 
                  environment. No generic content—just focused, relevant learning that addresses your exact requirements.
                </p>
                <div className="benefit-highlights">
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Custom content for your industry
                  </span>
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Flexible curriculum adaptation
                  </span>
                </div>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fa-solid fa-hands-helping"></i>
                </div>
                <h3>Hands-On Learning Approach</h3>
                <p>
                  Interactive workshops with practical exercises, real-world projects, and immediate application. 
                  Participants leave with confidence and skills they can implement right away.
                </p>
                <div className="benefit-highlights">
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Interactive labs and exercises
                  </span>
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Learn by doing, not just listening
                  </span>
                </div>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fa-solid fa-book-open"></i>
                </div>
                <h3>Comprehensive Materials</h3>
                <p>
                  Participants receive detailed training materials, reference guides, code examples, and resources 
                  for continued learning. Take-home materials ensure knowledge retention and ongoing development.
                </p>
                <div className="benefit-highlights">
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Detailed handbooks and guides
                  </span>
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Code samples and templates
                  </span>
                </div>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fa-solid fa-users"></i>
                </div>
                <h3>Team Collaboration Focus</h3>
                <p>
                  Training programs emphasize collaboration, communication, and team dynamics. Build not just 
                  technical skills, but also the soft skills essential for modern workplace success.
                </p>
                <div className="benefit-highlights">
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Enhanced team communication
                  </span>
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Collaborative problem-solving
                  </span>
                </div>
              </div>

              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fa-solid fa-headset"></i>
                </div>
                <h3>Ongoing Support</h3>
                <p>
                  Training doesn't end when the session concludes. Participants receive follow-up support, 
                  Q&A sessions, and access to additional resources to ensure successful implementation.
                </p>
                <div className="benefit-highlights">
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Post-training support included
                  </span>
                  <span className="highlight-item">
                    <i className="fa-solid fa-check"></i>
                    Follow-up Q&A sessions
                  </span>
                </div>
              </div>
            </div>

            <div className="benefits-summary">
              <div className="summary-content">
                <h3>Complete Learning Experience</h3>
                <p>
                  My training programs combine theoretical knowledge with practical application, ensuring your team 
                  gains both understanding and capability. From initial consultation to post-training support, 
                  every aspect is designed to maximize learning outcomes and organizational impact.
                </p>
                <div className="summary-stats">
                  <div className="stat-item">
                    <div className="stat-number-t">100%</div>
                    <div className="stat-label-t">Hands-On Content</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number-t">Custom</div>
                    <div className="stat-label-t">Tailored Programs</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number-t">Ongoing</div>
                    <div className="stat-label-t">Support Included</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Information Section */}
        <section className="pricing-information-section">
          <div className="pricing-info-content">
            <h2>Pricing Information</h2>
            <div className="pricing-disclaimer">
              <div className="pricing-disclaimer-icon">
                <i className="fa-solid fa-info-circle"></i>
              </div>
              <div className="pricing-disclaimer-text">
                <h3>Flexible & Transparent Pricing</h3>
                <p>
                  All prices listed above are <strong>approximate and negotiable</strong>. We understand that every 
                  organization has unique needs and circumstances, which is why we offer flexible pricing structures 
                  tailored to your specific requirements.
                </p>
              </div>
            </div>

            <div className="pricing-factors">
              <h3>Factors That May Influence Final Pricing:</h3>
              <div className="pricing-factors-grid">
                <div className="pricing-factor-card">
                  <div className="factor-icon">
                    <i className="fa-solid fa-calendar-alt"></i>
                  </div>
                  <h4>Timing & Advance Booking</h4>
                  <p>Early bookings often qualify for discounted rates. Last-minute requests may incur premium pricing.</p>
                </div>

                <div className="pricing-factor-card">
                  <div className="factor-icon">
                    <i className="fa-solid fa-users"></i>
                  </div>
                  <h4>Group Size</h4>
                  <p>Larger groups may benefit from economies of scale, while smaller, specialized sessions may have premium rates.</p>
                </div>

                <div className="pricing-factor-card">
                  <div className="factor-icon">
                    <i className="fa-solid fa-layer-group"></i>
                  </div>
                  <h4>Multiple Training Package</h4>
                  <p>Bundle multiple trainings or book sessions for different teams to unlock significant volume discounts.</p>
                </div>

                <div className="pricing-factor-card">
                  <div className="factor-icon">
                    <i className="fa-solid fa-map-marker-alt"></i>
                  </div>
                  <h4>Location & Travel</h4>
                  <p>Training location, travel requirements, and accommodation costs may affect the final pricing structure.</p>
                </div>

                <div className="pricing-factor-card">
                  <div className="factor-icon">
                    <i className="fa-solid fa-globe"></i>
                  </div>
                  <h4>Geographic Region</h4>
                  <p>Pricing may vary based on the country and city where training is delivered, considering local market conditions. I operate mainly in European region, but I am open to other locations too.</p>
                </div>

                <div className="pricing-factor-card">
                  <div className="factor-icon">
                    <i className="fa-solid fa-cogs"></i>
                  </div>
                  <h4>Customization Level</h4>
                  <p>Tailored content, industry-specific examples, or specialized requirements may influence pricing.</p>
                </div>
              </div>
            </div>

            <div className="pricing-benefits">
              <h3>Why Choose Our Flexible Pricing Approach?</h3>
              <div className="pricing-benefits-list">
                <div className="benefit-item">
                  <i className="fa-solid fa-check-circle"></i>
                  <span><strong>Transparent Communication:</strong> No hidden fees or surprise costs</span>
                </div>
                <div className="benefit-item">
                  <i className="fa-solid fa-check-circle"></i>
                  <span><strong>Budget-Friendly Options:</strong> We work within your budget constraints</span>
                </div>
                <div className="benefit-item">
                  <i className="fa-solid fa-check-circle"></i>
                  <span><strong>Value-Based Pricing:</strong> Pricing aligned with the value delivered to your organization</span>
                </div>
                <div className="benefit-item">
                  <i className="fa-solid fa-check-circle"></i>
                  <span><strong>Long-term Partnerships:</strong> Preferred rates for ongoing training relationships</span>
                </div>
              </div>
            </div>

            <div className="pricing-cta">
              <div className="pricing-cta-content">
                <h3>Ready to Get a Custom Quote?</h3>
                <p>
                  Contact us today to discuss your training needs and receive a personalized pricing proposal 
                  that fits your organization's requirements and budget.
                </p>
                <div className="pricing-cta-buttons">
                  <a href="/contact" className="pricing-quote-btn">
                    <i className="fa-solid fa-calculator"></i>
                    Get Custom Quote
                  </a>
                  <a href="mailto:contact@pawelpiwosz.com" className="pricing-email-btn">
                    <i className="fa-solid fa-envelope"></i>
                    Email Directly
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="contact-cta">
          <div className="cta-content">
            <h2>Ready to Invest in Your Team's Growth?</h2>
            <p>Let's discuss how our training programs can transform your organization's capabilities.</p>
            <a href="/contact" className="cta-button">Get in Touch</a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Trainings;